import { getApp } from 'https://www.gstatic.com/firebasejs/10.14.1/firebase-app.js';
import {
  getAuth,
  onAuthStateChanged,
  signOut,
  updateProfile,
  sendPasswordResetEmail,
} from 'https://www.gstatic.com/firebasejs/10.14.1/firebase-auth.js';

const auth = getAuth(getApp());
const root = document.querySelector('[data-profile]');

let originalName = '';
let messageTimer = null;

if (root) {
  onAuthStateChanged(auth, (user) => {
    if (!user) {
      // EAPAHvZu — one source: window.__studioAuth (snippets/studio-signin-url),
      // composed through the ONE helper emitted in layout/theme.liquid.
      window.location.replace(
        (window.__studioAuth && window.__studioAuth.withReturn)
          ? window.__studioAuth.withReturn('/pages/profile')
          : '/pages/signin?return_url=' + encodeURIComponent('/pages/profile')
      );
      return;
    }
    renderProfile(user);
  });
}

function renderProfile(user) {
  const loading = root.querySelector('[data-profile-loading]');
  const content = root.querySelector('[data-profile-content]');
  if (loading) loading.hidden = true;
  if (content) content.hidden = false;

  const name  = user.displayName || '';
  const email = user.email || '';

  const greetingEl = root.querySelector('[data-profile-greeting]');
  const nameEl      = root.querySelector('[data-profile-name]');
  const emailEl     = root.querySelector('[data-profile-email]');
  const nameInput   = root.querySelector('[data-name-input]');
  const firstName   = name.trim().split(/\s+/)[0];
  if (greetingEl) greetingEl.textContent = firstName ? `Welcome back, ${firstName}` : 'Welcome back';
  if (nameEl)     nameEl.textContent     = name || 'Welcome';
  if (emailEl)    emailEl.textContent    = email;
  if (nameInput)  nameInput.value        = name;

  originalName = name;
  const saveBtn = root.querySelector('[data-name-save]');
  if (saveBtn) saveBtn.disabled = true;

  renderAvatar(user, name, email);
  renderPasswordRow(user);
}

function renderAvatar(user, name, email) {
  const avatar = root.querySelector('[data-profile-avatar]');
  if (!avatar) return;

  if (user.photoURL) {
    avatar.innerHTML = '';
    const img = document.createElement('img');
    img.src = user.photoURL;
    img.alt = '';
    img.className = 'sp-avatar-img';
    img.onerror = () => renderInitials(avatar, name, email);
    avatar.appendChild(img);
    return;
  }

  renderInitials(avatar, name, email);
}

function renderInitials(avatar, name, email) {
  avatar.innerHTML = '';
  const source = name || email || '';
  const initials = source
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  avatar.textContent = initials || '?';
}

// Google-only accounts have no Firebase password to reset
function renderPasswordRow(user) {
  const resetBtn   = root.querySelector('[data-reset-password]');
  const passwordSub = root.querySelector('[data-password-sub]');
  const isPasswordUser = user.providerData.some((p) => p.providerId === 'password');

  if (resetBtn) resetBtn.hidden = !isPasswordUser;
  if (passwordSub) {
    passwordSub.textContent = isPasswordUser
      ? "We'll email you a secure reset link"
      : 'Managed by your Google account';
  }
}

// ── Display name form ────────────────────────────────────────────────────

root?.querySelector('[data-name-input]')?.addEventListener('input', (e) => {
  const saveBtn = root.querySelector('[data-name-save]');
  if (saveBtn) saveBtn.disabled = e.target.value.trim() === originalName;
});

root?.querySelector('[data-name-form]')?.addEventListener('submit', async (e) => {
  e.preventDefault();
  const input = e.target.querySelector('[data-name-input]');
  const btn   = e.target.querySelector('[type="submit"]');
  const user  = auth.currentUser;
  if (!input || !btn || !user) return;

  const newName = input.value.trim();
  btn.disabled  = true;
  clearMessage();
  try {
    await updateProfile(user, { displayName: newName });
    originalName = newName;
    const nameEl     = root.querySelector('[data-profile-name]');
    const greetingEl = root.querySelector('[data-profile-greeting]');
    const firstName  = newName.trim().split(/\s+/)[0];
    if (nameEl)     nameEl.textContent     = newName || 'Welcome';
    if (greetingEl) greetingEl.textContent = firstName ? `Welcome back, ${firstName}` : 'Welcome back';
    renderAvatar(user, newName, user.email || '');
    showMessage('Display name updated.', false);
  } catch (err) {
    showMessage('Could not update your name. Please try again.', true);
    btn.disabled = false;
  }
});

// ── Password reset ───────────────────────────────────────────────────────

root?.querySelector('[data-reset-password]')?.addEventListener('click', async (e) => {
  const user = auth.currentUser;
  if (!user || !user.email) return;

  const btn = e.currentTarget;
  const originalLabel = btn.textContent;
  btn.disabled = true;
  btn.textContent = 'Sending…';
  clearMessage();
  try {
    await sendPasswordResetEmail(auth, user.email);
    showMessage('Password reset email sent — check your inbox.', false);
  } catch (err) {
    showMessage('Could not send reset email. Please try again.', true);
  } finally {
    btn.disabled = false;
    btn.textContent = originalLabel;
  }
});

function showMessage(msg, isError) {
  const el = root.querySelector('[data-name-message]');
  if (!el) return;
  el.textContent = msg;
  el.hidden = false;
  el.classList.toggle('sp-message--error', isError);
  if (messageTimer) clearTimeout(messageTimer);
  messageTimer = setTimeout(clearMessage, 4000);
}

function clearMessage() {
  const el = root.querySelector('[data-name-message]');
  if (!el) return;
  el.hidden = true;
  el.classList.remove('sp-message--error');
}

// ── Log out ──────────────────────────────────────────────────────────────

root?.querySelector('[data-signout]')?.addEventListener('click', async () => {
  try {
    await signOut(auth);
    window.location.href = '/';
  } catch (err) {
    console.error('Sign out failed:', err);
  }
});
